<template>
  <div class="project-container">
    <div class="main-content">

      <!-- BETREUER ZUWEISEN (nur Lehrer/Admin) -->
      <div class="assign-teacher" v-if="isTeacher || isAdmin">
        <label>Projekt: </label>
        <input
          v-model="assignProjId"
          placeholder="ID"
          class="filter-input"
        />
        <template v-if="isTeacher">
          <button class="create-button" @click="assignTeacher('self')">
            Speichern
          </button>
        </template>
        <template v-else>
          <label>Lehrer:</label>
          <input
            v-model="assignTeacherSam"
            placeholder="SamAccount"
            class="filter-input"
          />
          <button class="create-button" @click="assignTeacher('admin')">
            Speichern
          </button>
        </template>
      </div>

      <!-- FILTER-BEREICH -->
      <div class="filters">
        <div class="search-wrapper">
          <span class="material-symbols-outlined search-icon">search</span>
          <input
            v-model="search"
            placeholder="Suchbegriff"
            class="search-input"
            @input="fetchProjectsDebounced"
          />
        </div>
        <div class="filter-actions">
          <button
            v-if="!hideRejected"
            class="toggle-rejected-button"
            @click="hideRejected = true"
          >
            Abgelehnte verbergen
          </button>
          <button
            v-else
            class="toggle-rejected-button"
            @click="hideRejected = false"
          >
            Abgelehnte anzeigen
          </button>
          <button
            class="create-button"
            @click="openCreateProjectModal"
            :disabled="!canCreate"
          >
            Projekt erstellen
          </button>
        </div>
      </div>

      <!-- PROJEKT-TABELLE -->
      <table v-if="displayedProjects.length" class="project-table">
        <thead>
          <tr>
            <th>Projektname</th>
            <th>Betreuer</th>
            <th>Start</th>
            <th>Ende</th>
            <th>Status</th>
            <th v-if="isAdmin">Aktionen</th>
            <th v-if="isAdmin">Review</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="project in displayedProjects"
            :key="project.projektId"
          >
            <td>
              <button
                class="project-name-button"
                @click="openViewProjectModal(project)"
              >
                {{ project.titel }}
              </button>
            </td>
            <td>{{ project.betreuerSamAccountName }}</td>
            <td>{{ formatDate(project.startdatum) }}</td>
            <td>{{ formatDate(project.enddatum) }}</td>
            <td>{{ project.status }}</td>
            <td v-if="isAdmin">
              <button
                class="icon-button"
                @click="openEditProjectModal(project)"
                title="Bearbeiten"
              >
                <span class="material-symbols-outlined">edit</span>
              </button>
              <button
                class="icon-button"
                @click="confirmDeleteProject(project)"
                title="Löschen"
              >
                <span class="material-symbols-outlined delete-icon">delete</span>
              </button>
            </td>
            <td v-if="isAdmin">
              <div v-if="project.status === 'EINGEREICHT'">
                <button
                  class="accept-button"
                  @click="reviewProject(project, 'ACCEPTED')"
                >
                  Annehmen
                </button>
                <button
                  class="reject-button"
                  @click="openRejectModal(project)"
                >
                  Ablehnen
                </button>
              </div>
              <div v-else>-</div>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="no-projects">Keine Projekte gefunden.</p>

      <!-- SCHÜLER-BEREICH (4. Klasse) mit Pagination -->
      <div class="schueler-section">
        <div class="schueler-header">
          <h3>Schüler der 4. Klasse</h3>
          <button
            v-if="isAdmin"
            @click="refreshAndLoadSchueler"
            class="create-button"
          >
            Schüler aktualisieren
          </button>
        </div>

        <div v-if="isStudent && mySchueler">
          <h4>Dein Account: {{ mySchueler.samAccountName }}</h4>
        </div>
        <p class="small-status">
          {{ isSchueler4
            ? "Du bist Schüler der 4. Klasse"
            : "Du bist kein Schüler der 4. Klasse" }}
        </p>

        <table v-if="schuelerList.length" class="project-table">
          <thead>
            <tr>
              <th>SamAccountName</th>
              <th>Teammitglieder</th>
              <th>1. Wunsch</th>
              <th>2. Wunsch</th>
              <th>3. Wunsch</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="schueler in paginatedSchuelerList"
              :key="schueler.samAccountName"
              :class="{ 'highlight-row': schueler.samAccountName === authStore.samAccount }"
            >
              <td>{{ schueler.samAccountName }}</td>
              <td>
                <input
                  type="text"
                  v-model="schueler.teamMitglieder"
                  style="width: 90%;"
                  placeholder="user1,user2"
                  :disabled="!isEditable(schueler.samAccountName)"
                />
              </td>
              <td>
                <select
                  v-model="schueler.wish1"
                  :disabled="!isEditable(schueler.samAccountName)"
                >
                  <option value="">Keine Auswahl</option>
                  <option
                    v-for="proj in approvedProjects"
                    :key="proj.projektId"
                    :value="proj.projektId"
                  >
                    {{ proj.titel }}
                  </option>
                </select>
              </td>
              <td>
                <select
                  v-model="schueler.wish2"
                  :disabled="!isEditable(schueler.samAccountName)"
                >
                  <option value="">Keine Auswahl</option>
                  <option
                    v-for="proj in approvedProjects"
                    :key="proj.projektId"
                    :value="proj.projektId"
                  >
                    {{ proj.titel }}
                  </option>
                </select>
              </td>
              <td>
                <select
                  v-model="schueler.wish3"
                  :disabled="!isEditable(schueler.samAccountName)"
                >
                  <option value="">Keine Auswahl</option>
                  <option
                    v-for="proj in approvedProjects"
                    :key="proj.projektId"
                    :value="proj.projektId"
                  >
                    {{ proj.titel }}
                  </option>
                </select>
              </td>
              <td>
                <button
                  class="save-button"
                  @click="updateSchuelerData(schueler)"
                  :disabled="!isEditable(schueler.samAccountName)"
                >
                  Speichern
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="no-projects">Keine Schülerdaten verfügbar.</p>

        <div v-if="totalPagesSchueler > 1" class="pagination">
          <button
            @click="goToPrevPageSchueler"
            :disabled="currentPageSchueler === 1"
          >
            Letztes
          </button>
          <button
            v-for="page in pagesSchueler"
            :key="page"
            @click="changePageSchueler(page)"
            :class="['page-button', { 'active-page': page === currentPageSchueler }]"
          >
            {{ page }}
          </button>
          <button
            @click="goToNextPageSchueler"
            :disabled="currentPageSchueler === totalPagesSchueler"
          >
            Nächstes
          </button>
        </div>
      </div>
    </div>

    <!-- CREATE PROJECT MODAL -->
    <div v-if="showCreateProjectModal" class="modal-overlay">
      <div class="modal">
        <span class="close" @click="closeCreateProjectModal">&times;</span>
        <h2>Neues Projekt erstellen</h2>
        <form @submit.prevent="createProject" class="modal-form">
          <label>Titel:</label>
          <input type="text" v-model="newProject.titel" required />
          <label>Beschreibung:</label>
          <input type="text" v-model="newProject.beschreibung" />
          <label>Startdatum:</label>
          <input type="date" v-model="newProject.startdatum" required />
          <label>Enddatum:</label>
          <input type="date" v-model="newProject.enddatum" required />
          <div v-if="isAdmin">
            <label>BetreuerSamAccountName (optional):</label>
            <input type="text" v-model="newProject.betreuerSamAccountName" />
          </div>
          <div>
            <label>
              Lastenheft (PDF)
              <span v-if="isAdmin">(optional)</span>:
            </label>
            <input
              type="file"
              accept="application/pdf"
              @change="handleLastenheftFile"
              :required="isStudent"
            />
          </div>
          <button type="submit" class="submit-button">Projekt erstellen</button>
        </form>
      </div>
    </div>

    <!-- EDIT PROJECT MODAL -->
    <div v-if="showEditProjectModal" class="modal-overlay">
      <div class="modal">
        <span class="close" @click="closeEditProjectModal">&times;</span>
        <h2>Projekt bearbeiten</h2>
        <form @submit.prevent="updateProject" class="modal-form">
          <label>Titel:</label>
          <input type="text" v-model="editProject.titel" required />
          <label>Beschreibung:</label>
          <input type="text" v-model="editProject.beschreibung" />
          <label>Startdatum:</label>
          <input type="date" v-model="editProject.startdatum" required />
          <label>Enddatum:</label>
          <input type="date" v-model="editProject.enddatum" required />
          <label>BetreuerSamAccountName:</label>
          <input type="text" v-model="editProject.betreuerSamAccountName" />
          <button type="submit" class="submit-button">Speichern</button>
        </form>
      </div>
    </div>

    <!-- DELETE CONFIRMATION MODAL -->
    <div v-if="showDeleteConfirmationModal" class="modal-overlay">
      <div class="modal">
        <span class="close" @click="closeDeleteConfirmationModal">&times;</span>
        <h2>Projekt löschen</h2>
        <p>Möchten Sie das Projekt "{{ projectToDelete.titel }}" wirklich löschen?</p>
        <div class="modal-actions">
          <button class="confirm-button" @click="deleteProject">
            Ja, löschen
          </button>
          <button class="cancel-button" @click="closeDeleteConfirmationModal">
            Abbrechen
          </button>
        </div>
      </div>
    </div>

    <!-- VIEW PROJECT MODAL -->
    <div v-if="showViewProjectModal" class="modal-overlay">
      <div class="modal">
        <span class="close" @click="closeViewProjectModal">&times;</span>
        <h2>Projektdetails</h2>
        <div class="project-details">
          <p><strong>Titel:</strong> {{ viewProject.titel }}</p>
          <p><strong>Beschreibung:</strong> {{ viewProject.beschreibung }}</p>
          <p><strong>Betreuer:</strong> {{ viewProject.betreuerSamAccountName }}</p>
          <p>
            <strong>Team:</strong>
            {{ (viewProject.mitarbeiterSamAccountNames || []).join(", ") }}
          </p>
          <p><strong>Status:</strong> {{ viewProject.status }}</p>
          <p v-if="viewProject.ablehnungsgrund && viewProject.status === 'ABGELEHNT'">
            <strong>Ablehnungsgrund:</strong> {{ viewProject.ablehnungsgrund }}
          </p>
        </div>
        <h3>Bewerbungen</h3>
        <div v-if="viewProjectApplicants.length">
          <p>
            Wunsch 1:
            {{ getApplicantsByPriority(1).length
              ? getApplicantsByPriority(1).join(", ")
              : "-" }}
          </p>
          <p>
            Wunsch 2:
            {{ getApplicantsByPriority(2).length
              ? getApplicantsByPriority(2).join(", ")
              : "-" }}
          </p>
          <p>
            Wunsch 3:
            {{ getApplicantsByPriority(3).length
              ? getApplicantsByPriority(3).join(", ")
              : "-" }}
          </p>
        </div>
        <p v-else>Keine Bewerbungen vorhanden.</p>
      </div>
    </div>

    <!-- MODAL FÜR ABLEHNUNG (REJECT) -->
    <div v-if="showRejectModal" class="modal-overlay">
      <div class="modal">
        <span class="close" @click="closeRejectModal">&times;</span>
        <h2>Projekt ablehnen</h2>
        <p>Grund für Ablehnung:</p>
        <textarea
          v-model="rejectionReason"
          placeholder="Bitte den Grund eingeben..."
          rows="3"
          style="width: 100%;"
        ></textarea>
        <div class="modal-actions">
          <button class="confirm-button" @click="confirmReject">
            Bestätigen
          </button>
          <button class="cancel-button" @click="closeRejectModal">
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { ref, computed, onMounted } from "vue";
import debounce from "lodash.debounce";
import { useAuthenticationStore } from "@/stores/useAuthStore";

function convertToGermanDate(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${day}.${month}.${year}`;
}

export default {
  name: "Projekte",
  setup() {
    const authStore = useAuthenticationStore();

    // Zuweisung
    const assignProjId = ref("");
    const assignTeacherSam = ref("");

    // Filter & Projekte
    const search = ref("");
    const projects = ref([]);
    const hideRejected = ref(false);

    // Modals
    const showCreateProjectModal = ref(false);
    const showEditProjectModal = ref(false);
    const showDeleteConfirmationModal = ref(false);
    const showViewProjectModal = ref(false);
    const showRejectModal = ref(false);

    const newProject = ref({
      titel: "",
      beschreibung: "",
      status: "",
      startdatum: "",
      enddatum: "",
      betreuerSamAccountName: "",
      lastenheftFile: null
    });

    const editProject = ref({
      projektId: null,
      titel: "",
      beschreibung: "",
      status: "",
      startdatum: "",
      enddatum: "",
      betreuerSamAccountName: ""
    });

    const projectToDelete = ref({});
    const viewProject = ref({});
    const viewProjectApplicants = ref([]);
    const rejectionReason = ref("");
    const projectToReject = ref(null);

    const isLoading = ref(false);

    // Schüler-Daten + Pagination
    const schuelerList = ref([]);
    const itemsPerPageSchueler = ref(10);
    const currentPageSchueler = ref(1);
    const totalPagesSchueler = computed(() =>
      Math.ceil(schuelerList.value.length / itemsPerPageSchueler.value)
    );
    const paginatedSchuelerList = computed(() => {
      const start =
        (currentPageSchueler.value - 1) * itemsPerPageSchueler.value;
      return schuelerList.value.slice(
        start,
        start + itemsPerPageSchueler.value
      );
    });
    const pagesSchueler = computed(() => {
      const arr = [];
      for (let i = 1; i <= totalPagesSchueler.value; i++) arr.push(i);
      return arr;
    });
    function changePageSchueler(page) {
      currentPageSchueler.value = page;
    }
    function goToPrevPageSchueler() {
      if (currentPageSchueler.value > 1) currentPageSchueler.value--;
    }
    function goToNextPageSchueler() {
      if (currentPageSchueler.value < totalPagesSchueler.value)
        currentPageSchueler.value++;
    }

    // CSRF & Auth
    const csrfToken = ref("");
    const csrfHeaderName = ref("");
    const authToken = ref(
      document.cookie.match(/authToken=([^;]+)/)?.[1] || ""
    );

    async function loadCsrfToken() {
      try {
        const resp = await axios.get(`${process.env.VUE_APP_BACKEND}/auth/csrf-token`, {
          withCredentials: true
        });
        csrfToken.value = resp.data.token;
        csrfHeaderName.value = resp.data.headerName;
      } catch {
        alert("Fehler beim Laden des CSRF-Tokens.");
      }
    }

    // Rollen & Berechtigungen
    const canPerformActions = computed(
      () => authStore.highestRole === "ROLE_ADMIN"
    );
    const isStudent = computed(
      () => authStore.highestRole === "ROLE_STUDENT"
    );
    const isAdmin = computed(() => authStore.highestRole === "ROLE_ADMIN");
    const isTeacher = computed(
      () => authStore.highestRole === "ROLE_TEACHER"
    );
    const isSchueler4 = computed(() => {
      const allowed = [
        "schueler4AHIT",
        "schueler4BHIT",
        "schueler4CHIT",
        "schueler4DHIT"
      ];
      return (
        authStore.highestRole === "ROLE_STUDENT" &&
        authStore.roles.some(r => allowed.includes(r))
      );
    });
    const canCreate = computed(() =>
      ["ROLE_ADMIN", "ROLE_TEACHER"].includes(authStore.highestRole) ||
      (authStore.highestRole === "ROLE_STUDENT" && isSchueler4.value)
    );
    const displayedProjects = computed(() =>
      hideRejected.value
        ? projects.value.filter(p => p.status !== "ABGELEHNT")
        : projects.value
    );
    const approvedProjects = computed(() =>
      projects.value.filter(p => p.status === "ANGENOMMEN")
    );
    const mySchueler = computed(() =>
      schuelerList.value.find(s => s.samAccountName === authStore.cn)
    );
    const isEditable = name => name === authStore.samAccount;

    // Betreuer zuweisen
    const assignTeacher = async mode => {
      await loadCsrfToken();
      try {
        let resp;
        if (mode === "self") {
          resp = await axios.put(
            `${process.env.VUE_APP_BACKEND}/diplomdb/api/projects/assign-teacher-by-id/self`,
            null,
            {
              headers: { [csrfHeaderName.value]: csrfToken.value },
              params: { projectId: assignProjId.value },
              withCredentials: true
            }
          );
        } else {
          resp = await axios.put(
            `${process.env.VUE_APP_BACKEND}/diplomdb/api/projects/assign-teacher-by-id/admin`,
            null,
            {
              headers: { [csrfHeaderName.value]: csrfToken.value },
              params: {
                projectId: assignProjId.value,
                teacherSamAccountName: assignTeacherSam.value
              },
              withCredentials: true
            }
          );
        }
        alert(JSON.stringify(resp.data, null, 2));
      } catch (e) {
        alert(
          "Fehler: " +
            (e.response?.data?.message || e.message)
        );
      }
    };

    // Projekte laden
    const fetchProjectsDebounced = debounce(fetchProjects, 300);
    async function fetchProjects() {
      isLoading.value = true;
      try {
        const resp = await axios.get(`${process.env.VUE_APP_BACKEND}/diplomdb/api/projects`, {
          headers: {
            [csrfHeaderName.value]: csrfToken.value,
            Authorization: `Bearer ${authToken.value}`
          },
          params: {
            search: search.value,
            sortField: "projektId",
            sortDirection: "asc"
          },
          withCredentials: true
        });
        projects.value = resp.data;
      } catch {
        alert("Fehler beim Laden der Projekte.");
      } finally {
        isLoading.value = false;
      }
    }

    // Datum formatieren
    const formatDate = ds => convertToGermanDate(ds);

    // Create Project
    function openCreateProjectModal() {
      if (canCreate.value) {
        newProject.value.status = isStudent.value ? "EINGEREICHT" : "ANGENOMMEN";
        showCreateProjectModal.value = true;
      } else {
        alert("Keine Berechtigung zum Erstellen.");
      }
    }
    function closeCreateProjectModal() {
      showCreateProjectModal.value = false;
      newProject.value = {
        titel: "",
        beschreibung: "",
        status: "",
        startdatum: "",
        enddatum: "",
        betreuerSamAccountName: "",
        lastenheftFile: null
      };
    }
    function handleLastenheftFile(event) {
      const file = event.target.files[0];
      if (!file) return;
      newProject.value.lastenheftFile = file;
    }
    async function createProject() {
      try {
        let resp;
        if (newProject.value.lastenheftFile) {
          const formData = new FormData();
          formData.append("titel", newProject.value.titel);
          formData.append("beschreibung", newProject.value.beschreibung);
          formData.append("startDatum", newProject.value.startdatum);
          formData.append("endDatum", newProject.value.enddatum);
          formData.append("lastenheft", newProject.value.lastenheftFile);
          resp = await axios.post(
            `${process.env.VUE_APP_BACKEND}/diplomdb/api/projects/create-with-lastenheft`,
            formData,
            {
              headers: {
                [csrfHeaderName.value]: csrfToken.value,
                Authorization: `Bearer ${authToken.value}`,
                "Content-Type": "multipart/form-data"
              },
              withCredentials: true
            }
          );
        } else {
          const finalStatus = isStudent.value
            ? "EINGEREICHT"
            : newProject.value.status || "ANGENOMMEN";
          resp = await axios.post(
            `${process.env.VUE_APP_BACKEND}/diplomdb/api/projects`,
            {
              titel: newProject.value.titel,
              beschreibung: newProject.value.beschreibung,
              status: finalStatus,
              startdatum: newProject.value.startdatum,
              enddatum: newProject.value.enddatum,
              betreuerSamAccountName: newProject.value.betreuerSamAccountName
            },
            {
              headers: {
                [csrfHeaderName.value]: csrfToken.value,
                Authorization: `Bearer ${authToken.value}`,
                "Content-Type": "application/json"
              },
              withCredentials: true
            }
          );
        }
        const createdProject = resp.data;
        if (
          isAdmin.value &&
          newProject.value.betreuerSamAccountName &&
          createdProject.projektId
        ) {
          await assignTeacherToProject(
            createdProject.projektId,
            newProject.value.betreuerSamAccountName
          );
        }
        alert("Projekt erfolgreich erstellt.");
        closeCreateProjectModal();
        fetchProjects();
      } catch {
        alert("Fehler beim Erstellen des Projekts.");
      }
    }

    // Edit Project
    function openEditProjectModal(project) {
      if (!canPerformActions.value) {
        alert("Keine Berechtigung zum Bearbeiten.");
        return;
      }
      editProject.value = { ...project };
      showEditProjectModal.value = true;
    }
    function closeEditProjectModal() {
      showEditProjectModal.value = false;
      editProject.value = {
        projektId: null,
        titel: "",
        beschreibung: "",
        status: "",
        startdatum: "",
        enddatum: "",
        betreuerSamAccountName: ""
      };
    }
    async function updateProject() {
      if (!editProject.value.projektId) {
        alert("Ungültiges Projekt.");
        return;
      }
      try {
        await axios.put(
          `${process.env.VUE_APP_BACKEND}/diplomdb/api/projects/${editProject.value.projektId}`,
          {
            titel: editProject.value.titel,
            beschreibung: editProject.value.beschreibung,
            status: editProject.value.status,
            startdatum: editProject.value.startdatum,
            enddatum: editProject.value.enddatum,
            betreuerSamAccountName: editProject.value.betreuerSamAccountName
          },
          {
            headers: {
              [csrfHeaderName.value]: csrfToken.value,
              Authorization: `Bearer ${authToken.value}`,
              "Content-Type": "application/json"
            },
            withCredentials: true
          }
        );
        alert("Projekt erfolgreich aktualisiert.");
        closeEditProjectModal();
        fetchProjects();
      } catch {
        alert("Fehler beim Aktualisieren des Projekts.");
      }
    }

    // Delete Project
    function confirmDeleteProject(project) {
      if (!canPerformActions.value) {
        alert("Keine Berechtigung.");
        return;
      }
      projectToDelete.value = project;
      showDeleteConfirmationModal.value = true;
    }
    function closeDeleteConfirmationModal() {
      showDeleteConfirmationModal.value = false;
      projectToDelete.value = {};
    }
    async function deleteProject() {
      if (!projectToDelete.value.projektId) {
        alert("Ungültiges Projekt.");
        return;
      }
      try {
        await axios.delete(
          `${process.env.VUE_APP_BACKEND}/diplomdb/api/projects/${projectToDelete.value.projektId}`,
          {
            headers: {
              [csrfHeaderName.value]: csrfToken.value,
              Authorization: `Bearer ${authToken.value}`
            },
            withCredentials: true
          }
        );
        alert("Projekt gelöscht.");
        closeDeleteConfirmationModal();
        fetchProjects();
      } catch (error) {
        alert(
          "Fehler beim Löschen: " +
            (error.response?.data?.message || error.message)
        );
      }
    }

    // Review & Reject
    async function reviewProject(project, decision) {
      try {
        const resp = await axios.put(
          `${process.env.VUE_APP_BACKEND}/diplomdb/api/projects/${project.projektId}/review`,
          null,
          {
            headers: {
              [csrfHeaderName.value]: csrfToken.value,
              Authorization: `Bearer ${authToken.value}`
            },
            params: { decision, reason: "" },
            withCredentials: true
          }
        );
        if (resp.status === 200) {
          alert("Projekt wurde angenommen.");
          fetchProjects();
        }
      } catch {
        alert("Fehler beim Review des Projekts.");
      }
    }

    function openRejectModal(project) {
      projectToReject.value = project;
      rejectionReason.value = "";
      showRejectModal.value = true;
    }
    function closeRejectModal() {
      showRejectModal.value = false;
      projectToReject.value = null;
      rejectionReason.value = "";
    }
    async function confirmReject() {
      if (!projectToReject.value) return;
      try {
        const resp = await axios.put(
          `${process.env.VUE_APP_BACKEND}/diplomdb/api/projects/${projectToReject.value.projektId}/review`,
          null,
          {
            headers: {
              [csrfHeaderName.value]: csrfToken.value,
              Authorization: `Bearer ${authToken.value}`
            },
            params: { decision: "REJECTED", reason: rejectionReason.value },
            withCredentials: true
          }
        );
        if (resp.status === 200) {
          alert("Projekt wurde abgelehnt.");
          closeRejectModal();
          fetchProjects();
        }
      } catch {
        alert("Fehler beim Ablehnen des Projekts.");
      }
    }

    // View Project
    function closeViewProjectModal() {
      showViewProjectModal.value = false;
      viewProject.value = {};
      viewProjectApplicants.value = [];
    }
    function openViewProjectModal(project) {
      viewProject.value = { ...project };
      viewProjectApplicants.value = [];
      showViewProjectModal.value = true;
      fetchProjectApplications(project.projektId);
    }
    async function fetchProjectApplications(projektId) {
      try {
        const resp = await axios.get(
          `${process.env.VUE_APP_BACKEND}/diplomdb/api/project-applications`,
          {
            headers: {
              [csrfHeaderName.value]: csrfToken.value,
              Authorization: `Bearer ${authToken.value}`
            },
            params: { projektId },
            withCredentials: true
          }
        );
        viewProjectApplicants.value = resp.data;
      } catch {
        viewProjectApplicants.value = [];
      }
    }
    function getApplicantsByPriority(priority) {
      return viewProjectApplicants.value
        .filter(app => app.prioritaet === priority)
        .map(app => app.samAccountName);
    }

    // Schüler refresh & load
    async function refreshAndLoadSchueler() {
      if (!isAdmin.value) return;
      try {
        await loadCsrfToken();
        await axios.post(
          `${process.env.VUE_APP_BACKEND}/diplomdb/api/schueler/refresh?year=4`,
          {},
          {
            headers: {
              "Content-Type": "application/json",
              [csrfHeaderName.value]: csrfToken.value,
              Authorization: `Bearer ${authToken.value}`
            },
            withCredentials: true
          }
        );
        alert("Aktualisierung der Schüler der 4. Klasse abgeschlossen!");
      } catch (error) {
        alert("Fehler beim Aktualisieren der Schüler: " + error.message);
      }
    }
    async function fetchSchueler() {
      try {
        const resp = await axios.get(`${process.env.VUE_APP_BACKEND}/diplomdb/api/schueler`, {
          headers: {
            [csrfHeaderName.value]: csrfToken.value,
            Authorization: `Bearer ${authToken.value}`
          },
          withCredentials: true
        });
        const oldMap = new Map();
        for (const oldS of schuelerList.value) {
          oldMap.set(oldS.samAccountName, oldS);
        }
        let newList = resp.data.map(serverS => {
          const existing = oldMap.get(serverS.samAccountName);
          if (existing) {
            return {
              ...serverS,
              teamMitglieder: existing.teamMitglieder,
              wish1: existing.wish1,
              wish2: existing.wish2,
              wish3: existing.wish3
            };
          } else {
          return {
            ...serverS,
            teamMitglieder: "",
            wish1: "",
            wish2: "",
            wish3: ""
          };
        }
      });

      // Falls der eingeloggte 4.-Klasse-Schüler in der Liste, ganz nach vorne
      if (isSchueler4.value) {
        const idx = newList.findIndex(s => s.samAccountName === authStore.cn);
        if (idx >= 0) {
          const [me] = newList.splice(idx, 1);
          newList.unshift(me);
          currentPageSchueler.value = 1;
        }
      }

      schuelerList.value = newList;
    } catch (error) {
      schuelerList.value = [];
    }
  }

  // Bewerbungsdaten speichern
  async function updateSchuelerData(schueler) {
    const members = schueler.teamMitglieder
      .split(",")
      .map(x => x.trim())
      .filter(x => x);
    const wishes = [
      { wish: schueler.wish1, priority: 1 },
      { wish: schueler.wish2, priority: 2 },
      { wish: schueler.wish3, priority: 3 }
    ];
    let ok = true;
    for (const w of wishes) {
      if (w.wish) {
        try {
          await axios.post(
            `${process.env.VUE_APP_BACKEND}/diplomdb/api/project-applications`,
            {
              projektId: parseInt(w.wish),
              samAccountName: schueler.samAccountName,
              prioritaet: w.priority,
              teamMitglieder: members
            },
            {
              headers: {
                "Content-Type": "application/json",
                [csrfHeaderName.value]: csrfToken.value,
                Authorization: `Bearer ${authToken.value}`
              },
              withCredentials: true
            }
          );
        } catch {
          ok = false;
        }
      }
    }
    alert(ok ? "Bewerbung erfolgreich gespeichert." : "Fehler beim Speichern der Bewerbung.");
  }

  // Fallback: Admin weist Lehrer einem Projekt zu
  async function assignTeacherToProject(projectId, teacherSamAccountName) {
    if (!teacherSamAccountName) {
      alert("Bitte einen Betreuer eingeben.");
      return;
    }
    try {
      const resp = await axios.put(
        `${process.env.VUE_APP_BACKEND}/diplomdb/api/projects/assign-teacher-by-id/admin`,
        null,
        {
          headers: {
            [csrfHeaderName.value]: csrfToken.value,
            Authorization: `Bearer ${authToken.value}`
          },
          params: { projectId, teacherSamAccountName },
          withCredentials: true
        }
      );
      if (resp.status === 200) {
        alert("Betreuer erfolgreich zugewiesen.");
        fetchProjects();
      }
    } catch {
      alert("Fehler beim Zuweisen des Betreuers.");
    }
  }

  // Lifecycle
  onMounted(async () => {
    await authStore.checkAuth();
    await loadCsrfToken();
    fetchProjects();
    fetchSchueler();
  });

  // Exponierte Werte und Methoden
  return {
    authStore,
    assignProjId,
    assignTeacherSam,
    assignTeacher,
    isTeacher,
    isAdmin,
    isStudent,
    search,
    projects,
    hideRejected,
    displayedProjects,
    approvedProjects,
    formatDate,
    fetchProjectsDebounced,
    openCreateProjectModal,
    closeCreateProjectModal,
    newProject,
    handleLastenheftFile,
    createProject,
    showCreateProjectModal,
    openEditProjectModal,
    closeEditProjectModal,
    editProject,
    updateProject,
    showEditProjectModal,
    confirmDeleteProject,
    closeDeleteConfirmationModal,
    deleteProject,
    showDeleteConfirmationModal,
    projectToDelete,
    reviewProject,
    openRejectModal,
    closeRejectModal,
    confirmReject,
    showRejectModal,
    projectToReject,
    openViewProjectModal,
    closeViewProjectModal,
    viewProject,
    viewProjectApplicants,
    fetchProjectApplications,
    getApplicantsByPriority,
    showViewProjectModal,
    rejectionReason,
    isLoading,
    schuelerList,
    itemsPerPageSchueler,
    currentPageSchueler,
    totalPagesSchueler,
    paginatedSchuelerList,
    pagesSchueler,
    changePageSchueler,
    goToPrevPageSchueler,
    goToNextPageSchueler,
    refreshAndLoadSchueler,
    fetchSchueler,
    mySchueler,
    isSchueler4,
    updateSchuelerData,
    canCreate,
    canPerformActions,
    isEditable,
    assignTeacherToProject
  };
}
};
</script>

<style scoped>
/* 1) Inneren Container der Betreuer-Sektion in eine Zeile packen */
.assign-teacher {
  display: flex;            /* falls noch nicht gesetzt */
  justify-content: center;  /* zentriert die Kinder horizontal */
  align-items: center;      /* zentriert vertikal */
  gap: 10px;
  margin-bottom: 20px;
}
/* 2) Einheitliches Styling für die beiden Inputs */
.assign-teacher input {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-family: 'DM Sans', sans-serif;
  width: auto;
}

/* 3) Feste Breiten für bessere Optik */
.assign-teacher input#assignProjId {
  width: 100px;   /* Projekt-ID */
}
.assign-teacher input#assignTeacherSam {
  width: 150px;   /* Lehrer-SamAccountName */
}

/* 4) Button inline neben den Inputs */
.assign-teacher button {
  background-color: #5841ee;
  border: none;
  color: #fff;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  font-family: 'DM Sans', sans-serif;
  transition: background-color 0.3s;
  outline: none;
}

/* 5) Hover-Zustand */
.assign-teacher button:hover {
  background-color: #4a35d2;
}
textarea, button {
  font-family: 'DM Sans', sans-serif;
}
.project-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 90vh;
  font-family: 'DM Sans', sans-serif;
}

.main-content {
  width: 80%;
  max-width: 1000px;
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  margin: 20px auto;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.filters {
  display: flex;
  align-items: center;
  justify-content: space-between; /* optional, sorgt für Abstand links/rechts */
  gap: 10px;                      /* Abstand zwischen Suchfeld und Buttons */
}


.filter-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;     /* Buttons schrumpfen nicht */
}

.toggle-rejected-button {
  background-color: #fbbf24;
  border: none;
  color: #1f2937;
  padding: 8px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  margin: 5px;
  transition: background-color 0.3s;
}
.toggle-rejected-button:hover {
  background-color: #f59e0b;
}

.filter-input {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin: 5px;
  font-size: 14px;
  font-family: 'DM Sans', sans-serif;
}



.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 20px;
  color: #666;
  pointer-events: none; /* Icon nicht klickbar */
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;            /* nimmt so viel Platz wie möglich ein */
  min-width: 150px;   /* optional: damit es nicht ganz zusammenschrumpft */
}

.search-input {
  width: 100%;        /* füllt den Wrapper aus */
  padding: 8px 12px 8px 40px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
}

.create-button {
  background-color: #3b82f6;
  border: none;
  color: white;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin: 5px;
  font-family: 'DM Sans', sans-serif;
  transition: background-color 0.3s;
}
.create-button:hover {
  background-color: #2563eb;
}
.create-button:disabled {
  background-color: #a5b4fc;
  cursor: not-allowed;
}

.project-table {
  width: 100%;
  border-collapse: collapse;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 30px;
  font-size: 14px;
  font-family: 'DM Sans', sans-serif;
}

.project-table th,
.project-table td {
  text-align: center;
  padding: 12px;
  border-bottom: 1px solid #ddd;
}
.project-table thead tr {
  background-color: #f4f4f4;
}

.project-table tbody tr:nth-child(odd) {
  background-color: #F7F6FE;
}
.project-table tbody tr:nth-child(even) {
  background-color: #ffffff;
}
.project-table tbody tr:hover {
  background-color: #eaeafc;
}

/* Markierung der Zeile, in der der eingeloggte Benutzer vorkommt */
.highlight-row {
  background-color: #e0ffe0;
}

.no-projects {
  text-align: center;
  margin: 20px 0;
  font-size: 16px;
  color: #777;
  font-family: 'DM Sans', sans-serif;
}

.icon-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  margin-right: 5px;
  font-size: 1.2em;
  font-family: 'DM Sans', sans-serif;
  color: #5841ee;
  transition: opacity 0.2s;
}
.icon-button:hover {
  opacity: 0.7;
}
.delete-icon {
  color: red;
}
.project-name-button {
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  text-decoration: underline;
  font-family: 'DM Sans', sans-serif;
  padding: 0;
}
.project-name-button:hover {
  color: #2563eb;
}

.accept-button {
  background-color: #10b981;
  border: none;
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  margin-right: 5px;
  cursor: pointer;
}
.accept-button:hover {
  background-color: #059669;
}
.reject-button {
  background-color: #ef4444;
  border: none;
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
}
.reject-button:hover {
  background-color: #dc2626;
}

.schueler-section {
  margin-top: 40px;
}

/* Neu hinzugefügter Container für Titel + Button */
.schueler-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.schueler-section h3 {
  margin-bottom: 0; /* Wir reduzieren hier etwas, da der Container selbst margin-bottom hat */
  font-family: 'DM Sans', sans-serif;
  font-weight: bold;
}

.small-status {
  font-size: 0.9em;
  color: #555;
  margin: 5px 0 15px;
}

input[type="text"],
select {
  font-family: 'DM Sans', sans-serif;
}

.save-button {
  background-color: #10b981;
  border: none;
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  transition: background-color 0.3s;
}
.save-button:hover {
  background-color: #059669;
}
.save-button:disabled {
  background-color: #6ee7b7;
  cursor: not-allowed;
}

.pagination {
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: 'DM Sans', sans-serif;
}
.pagination button {
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  background-color: #e1e1e1;
  color: #000;
  font-weight: normal;
  transition: background-color 0.3s;
  border: none;
  font-family: 'DM Sans', sans-serif;
}
.pagination button:hover {
  background-color: #d4d4d4;
}
.pagination button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.page-button.active-page {
  background-color: #5841ee;
  color: #fff;
  font-weight: bold;
}
.page-button.active-page:hover {
  background-color: #4a35d2;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
.modal {
  background-color: #fff;
  padding: 20px;
  border-radius: 6px;
  min-width: 350px;
  max-width: 500px;
  position: relative;
  font-family: 'DM Sans', sans-serif;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
.close {
  font-size: 24px;
  font-weight: bold;
  position: absolute;
  right: 15px;
  top: 10px;
  cursor: pointer;
}
.modal-form {
  display: flex;
  flex-direction: column;
}
.modal-form label {
  margin-top: 10px;
  font-weight: bold;
  font-family: 'DM Sans', sans-serif;
}
.modal-form input {
  padding: 8px;
  margin-top: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: 'DM Sans', sans-serif;
}
.submit-button {
  margin-top: 20px;
  background-color: #5841ee;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  transition: background-color 0.3s;
}
.submit-button:hover {
  background-color: #4a35d2;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.confirm-button {
  background-color: #ef4444;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
}
.confirm-button:hover {
  background-color: #dc2626;
}
.cancel-button {
  background-color: #ccc;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
}
.cancel-button:hover {
  background-color: #bbb;
}
.project-details p {
  margin: 5px 0;
}
</style>
